import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { ACTION_MENU_TYPE } from '@/shared/constants/actionMenu';
import type { GedItem, StateEdc } from '@/shared/API/Workspace/models';
import type { GetGedItemResponse, GetFolderInfoResponse, WorkflowDataResponse, GetWorkspaceConfigResponse, LoadGedResponse, GetItemCountResponse } from '@/shared/DTO/GED';
import type { WorkspaceColumn } from '@/modules/workspace/components/WorkspaceColumnsMenu.vue';
import { GED_TYPE, isFileItemType } from '@/shared/constants/ged';
import { useFileHelper } from '@/shared/utils/fileHelper';
import type { ActionsRecord } from '@/modules/workspace/components/WorkspaceActionMenu.vue';
import type { Authorizations } from '@/shared/DTO/ADM';
import { WORKSPACE_CONFIG } from '@/shared/constants/workspaceConfig';

export const useWorkspaceStore = defineStore('workspace', () => {
    const { getIcon: getFileIcon } = useFileHelper();

    // Selection
    const selectedItem = ref<GedItem>({} as GedItem);
    const selectedItemInfo = ref<GetFolderInfoResponse>({} as GetFolderInfoResponse);
    const selection = ref<GedItem[]>([]);

    // Workspace
    const readonly = computed(() => {
        return currentWorkspace.value.id !== selectedItem.value.workspace;
    });
    const isGedItemsLoading = ref(false);
    const isNavigatingFromUrl = ref(false);
    const currentWorkspace = ref<GedItem>({} as GedItem);
    const setCurrentWorkspace = (newWorkspace: GedItem) => {
        currentWorkspace.value = newWorkspace;
        selectedItem.value = newWorkspace;
    };
    const lastItemCountResponse = ref<GetItemCountResponse | null>(null);
    const lastLoadedGedData = ref<LoadGedResponse | null>(null);

    // GED
    const itemCountInFolder = ref(0);
    const gedItems = ref<GedItem[]>([]);
    const temporaryParent = ref<GedItem>({} as GedItem);
    const temporaryItems = ref<GedItem[]>([]);

    /**
     * Creates a new temporary parent GedItem with the given id and children.
     * @param id - The id of the new item.
     * @param children - The children of the new item.
     * @returns The newly created temporary parent GedItem.
     */
    function createTempParentGedItem(id: number, children: GedItem[]): GedItem {
        return {
            key: id.toString(),
            keyPath: [],
            leaf: true,
            itemType: GED_TYPE.ELEMENT,
            children,
        };
    }

    /**
     * Creates a new temporary GedItem with the given id.
     * @param id - The id of the new item.
     * @returns The newly created temporary GedItem.
     */
    function createTempGedItem(id: number): GedItem {
        return {
            key: id.toString(),
            keyPath: [],
            leaf: true,
            itemType: GED_TYPE.UNKNOWN,
        };
    }

    /**
     * Creates a new GedItem with empty metadata and adds it as a child of the given parent.
     * If `id` is provided, the item key is `id.toString()`; otherwise a temporary key is used.
     * @param parent - The parent GedItem to attach the new child to.
     * @param id - Optional backend id for the new item; if omitted, a temporary key is generated.
     * @returns The newly created and attached GedItem.
     */
    function addGedItemChild(parent: GedItem, id: number): GedItem {
        const key = id.toString();
        const parentKeyPath = parent.keyPath ?? [];
        const newItem: GedItem = {
            key,
            keyPath: [...parentKeyPath, parent.key],
            leaf: true,
            itemType: GED_TYPE.UNKNOWN,
        };

        setGedItems([newItem], parent);
        return newItem;
    }

    /**
     * Retrieves a GedItem from the store by its key path.
     * @param path - The key path to the item.
     * @returns The GedItem if found, otherwise undefined.
     */
    function getItem( path: string[] ): GedItem | undefined {
        if (!path.length) {
            return undefined;
        }

        let siblings: GedItem[] | undefined = gedItems.value;
        let current: GedItem | undefined;

        for (const key of path) {
            current = siblings?.find(item => item.key === key);
            if (!current) {
                return undefined;
            }
            siblings = current.children;
        }

        return current;
    };

    /**
     * Retrieves a GedItem from the store by its path.
     * @param path - The path to the item.
     * @returns The GedItem if found, otherwise undefined.
     */
    function getItemByPath( path: string[] ): GedItem | undefined {
        const findItem = (items: GedItem[] | undefined): GedItem | undefined => {
            if (!items) {
                return undefined;
            }
            for (const item of items) {
                if (item.keyPath.length === path.length && item.keyPath.every((key, i) => key === path[i])) {
                    return item;
                }
                const found = findItem(item.children);
                if (found) {
                    return found;
                }
            }
            return undefined;
        };
        return findItem(gedItems.value);
    };

    /**
     * Retrieves the parent items of a given item by its path.
     * @param path - The path to the item.
     * @returns The parent items.
     */
    function getParentsItem( path: string[] ): GedItem[] {
        const parents: GedItem[] = [];
        if (path.length < 2) {
            return parents;
        }

        let siblings: GedItem[] | undefined = gedItems.value;

        for (const key of path.slice(0, -1)) {
            const item: GedItem | undefined = siblings?.find(item => item.key === key);
            if (!item) {
                return [];
            }
            parents.push(item);
            siblings = item.children;
        }

        return parents;
    };

    /**
     * Sets the GedItems in the store.
     * @param items - The items to set.
     * @param parent - The parent item.
     */
    function setGedItems(items: GedItem[], parent: GedItem): void {
        if (items.length === 0) {
            return;
        }

        const children = parent.children ?? (parent.children = []);
        const childrenByKey = new Map(children.map((child: GedItem) => [child.key, child]));

        for (const item of items) {
            const existing = childrenByKey.get(item.key);

            if (existing) {
                mergeGedItem(existing, item);
            } else {
                children.push(item);
            }
        }

        if (parent.key === 'root') {
            children.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? '')
            );
        }
    };

    /**
     * Updates the metadata of the GedItems in the store.
     * @param metadata - The metadata to update.
     * @param parent - The parent item.
     */
    function updateGedItemsMetadata(metadata: GetGedItemResponse[], parent: GedItem): void {
        const children = parent.children;
        if (!children?.length || metadata.length === 0) {
            return;
        }

        const metaByKey = new Map(metadata.map(data => [data.id.toString(), data]));

        for (const child of children) {
            const meta = metaByKey.get(child.key);

            if (!meta) {
                continue;
            }

            mergeGedItem(child, meta as GedItem);
        }
    };

    /**
     * Updates the workflow data of the GedItems in the store.
     * @param workflowData - The workflow data to update.
     */
    function updateGedItemsWorkflowData( workflowData: WorkflowDataResponse[] ): void {
        const children = selectedItem.value.children;
        if (!children?.length || workflowData.length === 0) {
            return;
        }

        const workflowByKey = new Map(workflowData.map(wf => [wf.id.toString(), wf]));

        for (const child of children) {
            const data = workflowByKey.get(child.key);

            if (!data) {
                continue;
            }

            child.workflowStatus = {
                step: data.step,
                missions: data.missions,
            };
        }
    };

    /**
     * Merges a source GedItem into a target GedItem.
     * @param target - The target GedItem.
     * @param source - The source GedItem.
     */
    function mergeGedItem( target: GedItem, source: GedItem ): void {
        const exludedKeys = [
            'leaf',
            'itemType',
            'icon',
            'keyPath',
            'isDraggable',
            'key',
            'children',
        ];
        for (const key of Object.keys(source)) {
            if (exludedKeys.includes(key)) {
                continue;
            }

            const src = source as Record<string, unknown>;
            const value = src[key];
            if (value === undefined || value === target[key as keyof GedItem]) {
                continue;
            }

            const targetValue = target[key as keyof GedItem];
            if (value && !Array.isArray(value) && typeof value === 'object' && targetValue) {
                (target as Record<string, unknown>)[key] = {
                    ...(targetValue as object),
                    ...value,
                };
            } else {
                (target as Record<string, unknown>)[key] = value;
            }
        }

        if (target.leaf === true && source.leaf === false) {
            target.leaf = source.leaf;
        }

        if (target.itemType === GED_TYPE.UNKNOWN && source.itemType !== GED_TYPE.UNKNOWN) {
            target.itemType = source.itemType;
            if (source.itemType === GED_TYPE.ELEMENT && !target.icon && !source.icon) {
                const hasCnOrWk = !!(source.cn || source.workflow);
                target.icon = hasCnOrWk ? 'ax ax-folder-color' : 'ax ax-folder-settings-1';
            }
        }

        if (target.itemType && isFileItemType(target.itemType)) {
            const icon = getFileIcon(source.format, target.itemType);
            if (icon !== target.icon) {
                target.icon = icon;
            }
        } else if (target.itemType === GED_TYPE.ELEMENT && source.icon !== undefined && source.icon !== target.icon) {
            target.icon = source.icon;
        }
    };

    /**
     * Initializes the GedItems in the store.
     * @param items - The items to initialize.
     */
    function initGedItems( items: GedItem[] ): void {
        gedItems.value = items;
        if (selectedItem.value?.keyPath) {
            selectedItem.value = getItem(selectedItem.value.keyPath) ?? selectedItem.value;
        }
    };

    /**
     * Updates the data of a GedItem in the store.
     * @param keyPath - The key path to the item.
     * @param data - The data to update.
     */
    function updateItem( keyPath: string[], data: Partial<GedItem> ): void {
        const item = getItem(keyPath);
        if (item) {
            Object.assign(item, data);
        }
    };

    // Actions
    const actionMenuState = reactive({
        event: null as Event | MouseEvent | null,
        type: '' as typeof ACTION_MENU_TYPE.CONTEXT_MENU | typeof ACTION_MENU_TYPE.TIERED_MENU,
        actions: {} as ActionsRecord,
        payload: [] as GedItem[],
    });
    const openActionMenu = (
        event: Event | MouseEvent,
        type: typeof ACTION_MENU_TYPE.CONTEXT_MENU | typeof ACTION_MENU_TYPE.TIERED_MENU,
        actions: ActionsRecord,
        payload: GedItem[],
    ) => {
        actionMenuState.event = event;
        actionMenuState.type = type;
        actionMenuState.actions = actions;
        actionMenuState.payload = payload;
    };
    // EDC
    const edcState = ref<StateEdc[]>([]);
    const hasEdcEnabled = computed(() => {
        const fromBim = workspaceConfig.value?.configs.includes(WORKSPACE_CONFIG.EDC);
        if (fromBim !== undefined && fromBim !== null) {
            return fromBim;
        }
        return edcState.value.length > 0;
    });

    // Columns
    const columns = ref<WorkspaceColumn[]>([
        {
            field: 'icons',
            headerClass: 'ax-non-resizable-column',
            hasCustomBody: true,
            style: 'min-width: 250px; max-width: 250px;',
            visible: true,
        },
        {
            field: 'id',
            header: 'workspace.columns.id',
            sortable: true,
            visible: false,
        },
        {
            field: 'ref',
            header: 'workspace.columns.ref',
            sortable: true,
            visible: true,
        },
        {
            field: 'label',
            header: 'workspace.columns.label',
            sortable: true,
            visible: true,
        },
        {
            field: 'description',
            header: 'workspace.columns.description',
            sortable: true,
            visible: true,
        },
        {
            field: 'format',
            header: 'workspace.columns.format',
            sortable: true,
            visible: true,
        },
        {
            field: 'edc',
            header: 'workspace.columns.edc',
            sortable: true,
            hasCustomBody: true,
            visible: true,
        },
        {
            field: 'workflowStatus',
            header: 'workspace.columns.status',
            style: 'min-width: 400px; max-width: 400px;',
            visible: true,
            hasCustomBody: true,
        },
        {
            field: 'version',
            header: 'workspace.columns.version',
            sortable: true,
            visible: true,
        },
        {
            field: 'dt_add',
            header: 'workspace.columns.dt_add',
            sortable: true,
            hasCustomBody: true,
            visible: true,
        },
        {
            field: 'add_by',
            header: 'workspace.columns.add_by',
            sortable: true,
            hasCustomBody: true,
            visible: true,
        },
        {
            field: 'dt_upd',
            header: 'workspace.columns.dt_upd',
            sortable: true,
            hasCustomBody: true,
            visible: true,
        },
        {
            field: 'upd_by',
            header: 'workspace.columns.upd_by',
            sortable: true,
            hasCustomBody: true,
            visible: true,
        },
        {
            field: 'link',
            header: 'workspace.columns.link',
            sortable: true,
            visible: true,
        },
        {
            field: 'size',
            header: 'workspace.columns.size',
            sortable: true,
            visible: true,
        },
        {
            field: 'workflow',
            header: 'workspace.columns.workflow',
            sortable: true,
            hasCustomBody: true,
            visible: true,
        },
        {
            field: 'lca',
            header: 'workspace.columns.lca',
            sortable: true,
            hasCustomBody: true,
            visible: true,
        },
        {
            field: 'more',
            frozen: true,
            alignFrozen: 'right',
            hasCustomBody: true,
            visible: true,
        },
    ]);

    // BIM Config
    const workspaceConfig = ref<GetWorkspaceConfigResponse | null>(null);

    // Obs Contexts
    const hasObsContexts = ref(false);

    // Current User Authorizations
    const currentUserAuthorizations = ref<Authorizations | null>(null);

    return {
        // Selection
        selectedItem,
        selectedItemInfo,
        selection,

        // Workspace
        readonly,
        currentWorkspace,
        isGedItemsLoading,
        isNavigatingFromUrl,
        setCurrentWorkspace,
        lastItemCountResponse,
        lastLoadedGedData,

        // GED
        itemCountInFolder,
        gedItems,
        createTempParentGedItem,
        createTempGedItem,
        addGedItemChild,
        getItem,
        getItemByPath,
        getParentsItem,
        setGedItems,
        initGedItems,
        updateGedItemsMetadata,
        updateGedItemsWorkflowData,
        temporaryParent,
        temporaryItems,

        // Actions
        actionMenuState,
        openActionMenu,
        updateItem,

        // EDC
        edcState,
        hasEdcEnabled,

        // Columns
        columns,

        // BIM Config
        workspaceConfig,

        // Obs Contexts
        hasObsContexts,

        // Current User Authorizations
        currentUserAuthorizations,
    };
});
